import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, useSchema } from '../../utils/seo';

export default function RealTalk() {
  useSEO({
    title: 'Real Talk - Die Wahrheit über Money-Apps | EarnProof',
    description: 'Keine geschönten Versprechen. Keine unrealistischen Erwartungen. Nur ehrliche Fakten darüber, was wirklich funktioniert und was nicht bei Earning-Apps.',
    keywords: 'earning apps wahrheit, money apps realität, online geld verdienen ehrlich, app scams vermeiden, realistische erwartungen',
    canonical: 'https://example.com/realtalk',
  });

  useSchema({
    type: 'Article',
    data: {
      headline: 'Real Talk: Die Wahrheit über Money-Apps',
      description: 'Ehrliche Einblicke in die Realität von Earning-Apps ohne geschönte Versprechen',
      author: {
        '@type': 'Person',
        name: 'Christopher Humpert',
      },
      publisher: {
        '@type': 'Organization',
        name: 'EarnProof',
        logo: {
          '@type': 'ImageObject',
          url: 'https://example.com/logo.png',
        },
      },
      datePublished: '2025-01-15',
      dateModified: '2025-01-15',
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-teal-400/20 text-teal-400 px-4 py-2 rounded-full mb-6 border border-teal-400/30">
            <i className="ri-chat-quote-line"></i>
            <span className="text-sm font-semibold">Ehrliche Einblicke</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Real Talk: Die Wahrheit über Money-Apps
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Keine geschönten Versprechen. Keine unrealistischen Erwartungen. Nur ehrliche Fakten darüber, was wirklich funktioniert und was nicht.
          </p>
        </div>
      </section>

      {/* Reality Check Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Die harte Realität</h2>
            <p className="text-xl text-gray-600">Was du wissen musst, bevor du anfängst</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-red-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-circle-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Mythos</h3>
                  <p className="text-gray-600">Reich werden über Nacht</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Viele Apps versprechen schnelles Geld ohne Aufwand. Die Realität: Es braucht Zeit, Geduld und eine durchdachte Strategie. Wer dir etwas anderes verspricht, lügt.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-circle-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Realität</h3>
                  <p className="text-gray-600">Solides Nebeneinkommen möglich</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Mit der richtigen Kombination aus Apps und konsequenter Nutzung sind 175-330€ monatlich realistisch. Das ist kein Vollzeitgehalt, aber ein solides Extra.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-red-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-circle-line text-2xl text-red-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Mythos</h3>
                  <p className="text-gray-600">Alle Apps sind gleich gut</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                90% der Money-Apps sind Zeitverschwendung mit minimalen Auszahlungen. Nur wenige Apps bieten wirklich faire Konditionen und lohnenswerte Verdienstmöglichkeiten.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-circle-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Realität</h3>
                  <p className="text-gray-600">Qualität vor Quantität</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Fokussiere dich auf 3-5 bewährte Apps statt 20 mittelmäßige. Mistplay, Freecash und Scrambly haben sich als zuverlässig und profitabel erwiesen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Time Investment Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Zeitaufwand vs. Verdienst</h2>
            <p className="text-xl text-gray-600">Was du wirklich investieren musst</p>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 border border-teal-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Anfängerphase (Monat 1-2)</h3>
                <span className="bg-teal-500 text-white px-4 py-2 rounded-full font-semibold">2-3h täglich</span>
              </div>
              <p className="text-gray-700 mb-4">
                Am Anfang brauchst du mehr Zeit, um die Apps kennenzulernen, Strategien zu testen und dein System aufzubauen.
              </p>
              <div className="flex items-center gap-2 text-teal-700">
                <i className="ri-money-euro-circle-line text-xl"></i>
                <span className="font-semibold">Erwarteter Verdienst: 80-150€/Monat</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl p-8 border border-cyan-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Fortgeschritten (Monat 3-6)</h3>
                <span className="bg-cyan-500 text-white px-4 py-2 rounded-full font-semibold">1-2h täglich</span>
              </div>
              <p className="text-gray-700 mb-4">
                Mit Erfahrung wirst du effizienter. Du weißt, welche Aufgaben sich lohnen und welche du ignorieren kannst.
              </p>
              <div className="flex items-center gap-2 text-cyan-700">
                <i className="ri-money-euro-circle-line text-xl"></i>
                <span className="font-semibold">Erwarteter Verdienst: 150-250€/Monat</span>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Optimiert (Monat 6+)</h3>
                <span className="bg-blue-500 text-white px-4 py-2 rounded-full font-semibold">30-60min täglich</span>
              </div>
              <p className="text-gray-700 mb-4">
                Mit etabliertem Referral-System und optimierten Routinen läuft vieles automatisch. Fokus auf High-Value-Aufgaben.
              </p>
              <div className="flex items-center gap-2 text-blue-700">
                <i className="ri-money-euro-circle-line text-xl"></i>
                <span className="font-semibold">Erwarteter Verdienst: 175-330€/Monat</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Signs Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Warnsignale: Diese Apps meiden</h2>
            <p className="text-xl text-gray-600">Erkenne unseriöse Anbieter sofort</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-alarm-warning-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Unrealistische Versprechen</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>"500€ in der ersten Woche"</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>"Ohne Aufwand Geld verdienen"</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>"Garantiertes Einkommen"</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-cross-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Verdächtige Praktiken</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Auszahlung erst ab 50€+</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Keine klaren Nutzungsbedingungen</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Zwang zu Referrals</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-error-warning-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Schlechte Bewertungen</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Unter 3.5 Sterne im Store</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Viele Auszahlungsprobleme</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-line text-red-500 mt-1"></i>
                  <span>Schlechter Support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bereit für die Wahrheit?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Schau dir meine geprüften App-Empfehlungen an und starte mit realistischen Erwartungen. Keine falschen Versprechen, nur bewährte Strategien.
          </p>
          <Link to="/reviews">
            <button type="button" className="inline-flex items-center justify-center font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap bg-gradient-to-r from-teal-400 to-cyan-400 text-gray-900 hover:from-teal-300 hover:to-cyan-300 shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl">
              Geprüfte Apps ansehen
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
